import {
  ChangeDetectorRef,
  Component,
  Output,
  EventEmitter,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormGroup } from '@angular/forms';
import { StripeCardComponent, StripeService } from 'ngx-stripe';
import {
  PaymentMethodsActions,
  PaymentMethodsApiActions,
} from '../../store/payment-methods/payment-methods.actions';
import { Store } from '@ngrx/store';
import { LanguageService } from 'app/services';
import { BankCardFormComponent } from '../bank-card-form/bank-card-form.component';
import { AsyncPipe } from '@angular/common';
import { ButtonComponent } from '@kinomap-dev/ng-ui-lib';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { selectPaymentMethods } from '../../store/payment-methods/payment-methods.selectors';
import { PaymentMethodResult } from '@stripe/stripe-js';
import { ToasterService } from 'app/services/toaster.service';

@Component({
  selector: 'app-bank-card-form-modal',
  templateUrl: './bank-card-form-modal.component.html',
  styleUrls: ['./bank-card-form-modal.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    BankCardFormComponent,
    AsyncPipe,
    ButtonComponent,
    TranslateModule,
  ],
})
export class BankCardFormModalComponent {
  bankCardForm: FormGroup | null = null;
  stripeCard: StripeCardComponent | null = null;
  subscriptions: Subscription[] = [];

  @Output() saveResult = new EventEmitter<{ success: boolean; error?: any }>();

  constructor(
    private cd: ChangeDetectorRef,
    private store: Store,
    private stripeService: StripeService,
    public languageService: LanguageService,
    private translateService: TranslateService,
    private toasterService: ToasterService
  ) { }

  onBankCardFormReady({
    bankCardForm,
    stripeCard,
  }: {
    bankCardForm: FormGroup | null;
    stripeCard: StripeCardComponent | null;
  }): void {
    this.bankCardForm = bankCardForm;
    this.stripeCard = stripeCard;
    this.cd.detectChanges();
  }

  async onSave(): Promise<void> {
    if (!this.bankCardForm?.valid) {
      this.saveResult.emit({ success: false, error: 'invalid_form' });
      return;
    }
    this.store.dispatch(PaymentMethodsActions.startLoadingPostPaymentMethod());

    const paymentId: string | null = await this.createStripePaymentMethodId();
    if (!paymentId) {
      this.toasterService.warning(
        null,
        this.translateService.instant(
          'dashboard.subscription.payment.register.error'
        )
      );

      this.saveResult.emit({ success: false, error: 'stripe_error' });
      return;
    }

    this.store.dispatch(
      PaymentMethodsApiActions.postPaymentMethod({ paymentId })
    );

    this.toasterService.success(
      null,
      this.translateService.instant(
        'dashboard.subscription.payment.register.sucessfull'
      )
    );

    this.saveResult.emit({ success: true });
  }

  private async createStripePaymentMethodId(): Promise<string | null> {
    const paymentMethodResult: PaymentMethodResult = await firstValueFrom(
      this.stripeService.createPaymentMethod({
        type: 'card',
        card: this.stripeCard.element,
        billing_details: {
          name: this.bankCardForm.get('name').value,
        },
      })
    );
    if (!!paymentMethodResult?.error) {
      this.store.dispatch(
        PaymentMethodsApiActions.failed({
          errorMessage: paymentMethodResult.error.message,
        })
      );
      return null;
    }
    if (!paymentMethodResult || !paymentMethodResult.paymentMethod) {
      return null;
    }

    return paymentMethodResult.paymentMethod.id;
  }

  private resetForms(): void {
    this.bankCardForm?.reset();
    this.stripeCard?.element?.clear();
  }

  ngOnInit(): void {
    this.store.dispatch(PaymentMethodsApiActions.fetchPaymentMethods());

    this.subscriptions.push(
      this.store.select(selectPaymentMethods).subscribe(() => {
        this.resetForms();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) =>
      subscription.unsubscribe()
    );
  }
}
